"use client";

import { useState } from "react";
import { Card } from "@tremor/react";
import type { TickerCardData, TickerCardsProps } from "./types";
import { WidgetInsights } from "../WidgetInsights";
import { usePopoverController } from "@/components/molecules/Popover/usePopoverController";
import { useEditWidget } from "@/modules/widgets/context/EditWidgetContext";
import { useDashboard } from "@/modules";
import { useUser } from "@/modules/auth/context/UserContext";
import { canDeleteWidget, canEditWidget, canDuplicateWidget, getEditDisabledReason, canViewAIInsights } from "@/modules/widgets/utils/permissions";
import { useWidgetGlobalOperations } from "@/modules/global/hooks/useWidgetGlobalOperations";
import { Tooltip } from "@/components/molecules/Tooltip";
import { AIStarsIcon, NoDataIcon, InfoIcon, ThreeDotsIcon, EditIcon, TrashIcon } from '@/components/icons';
import { Copy } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { NumberUtils } from "@/utils/NumberUtils";

interface CompactTickerProps extends TickerCardsProps {
    colors?: string[];
    dateRangeLabel?: string;
    disableTrend?: boolean;
    iconUrl?: string;
    hideInsights?: boolean;
    hideActions?: boolean;
    forceDecimals?: number;
    enableGradient?: boolean;
}

const defaultData: TickerCardData = {
    value: 125430,
    changeType: "positive",
};

export default function CompactTickerCard({ data = defaultData, title, dateRangeLabel, widget, onWidgetDelete, colors, error, disableTrend, iconUrl, hideInsights, hideActions, forceDecimals, enableGradient = false }: CompactTickerProps) {
    const insightsController = usePopoverController();
    const { openEditDrawer, openDuplicateDrawer } = useEditWidget();
    const { user, hydrated } = useUser();
    const { state: { selectedCompareEnabled } } = useDashboard();
    const [isHovered, setIsHovered] = useState(false);

    const canEdit = hydrated && canEditWidget(widget, user);
    const canDelete = hydrated && canDeleteWidget(widget, user);
    const canDuplicate = hydrated && canDuplicateWidget(widget, user);
    const disabledReason = hydrated ? getEditDisabledReason(widget, user) : null;
    const showInsights = !hideInsights && hydrated && widget && canViewAIInsights(widget, user);
    const { deleteWidget } = useWidgetGlobalOperations();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
        if (!widget) return;
        setIsDeleting(true);
        try {
            await deleteWidget(widget);
            onWidgetDelete?.();
        } catch (error) {
            console.error('Error deleting widget:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    // Always use the original accent color from props or widget
    const baseAccentColor = colors?.[0] || (widget as any)?.colors?.[0] || "#4f9a9b";
    const secondAccentColor = colors?.[1] || (widget as any)?.colors?.[1];
      
    // Determine if we should use gradient (when colors is an array with 2+ items AND enableGradient is true)
    const useGradient = enableGradient && colors && colors.length >= 2 && secondAccentColor;
       
    // Use gradient colors if available, otherwise fall back to regular colors
    const finalColor1 = useGradient ? colors[0] : baseAccentColor;
    const finalColor2 = useGradient ? colors[1] : baseAccentColor;

    const titleLower = title?.toLowerCase() || "";
    const isCostMetric = NumberUtils.isCurrencyMetric(titleLower);
    const isPercentageMetric = titleLower.includes("rate") || titleLower.includes("percentage") || titleLower.includes("%");

    const formatValue = (value: number) => {
        const formatted = NumberUtils.formatWithSeparators(value, titleLower.includes("spend") ? { decimals: 0 } : forceDecimals !== undefined ? { decimals: forceDecimals } : isCostMetric ? { decimals: 2 } : undefined);
        if (isCostMetric) return formatted.startsWith('$') ? formatted : `$${formatted}`;
        if (isPercentageMetric) return `${formatted}%`;
        return formatted;
    };

    const formatCompareValue = (value: number) => {
        const formatted = NumberUtils.formatWithSeparators(value, titleLower.includes("spend") ? { decimals: 0 } : forceDecimals !== undefined ? { decimals: forceDecimals } : isCostMetric ? { decimals: 2 } : undefined);
        if (isCostMetric) return formatted.startsWith('$') ? formatted : `$${formatted}`;
        if (isPercentageMetric) return `${formatted}%`;
        return formatted;
    };

    // Use default data if none provided
    const displayData = data || defaultData;

    const EPSILON = 1e-9;

    let currentValue = displayData.value;
    let previousValue: number | undefined = displayData?.previousPeriodComparison?.value;

    if (displayData?.periods?.length === 2) {
        const p0 = displayData.periods[0]?.value;
        const p1 = displayData.periods[1]?.value;

        if (typeof p0 === "number" && typeof p1 === "number") {
            const p0MatchesDisplayed = Math.abs(p0 - displayData.value) < EPSILON;
            const p1MatchesDisplayed = Math.abs(p1 - displayData.value) < EPSILON;

            if (p0MatchesDisplayed && !p1MatchesDisplayed) {
                currentValue = p0;
                previousValue = p1;
            } else {
                previousValue = p0;
                currentValue = p1;
            }
        }
    }

    const compareValue = previousValue;
    const showCompare = !disableTrend && !error && selectedCompareEnabled && typeof compareValue === "number";

    const delta = showCompare ? currentValue - compareValue : 0;
    const isUp = showCompare ? delta >= 0 : false;
    const isImprovement = showCompare ? (isCostMetric ? delta <= 0 : delta >= 0) : false;

    // Check if this is a no-data error (not a regular error)
    const widgetError = (widget as any)?.error;
    const isNoDataError = widgetError && widgetError.toLowerCase().includes('no data');

    return (
        <Card
            className="bg-white border border-[#c9d1d4] rounded-[16px] flex items-center relative p-4 pr-0 pt-3 overflow-hidden"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Left accent strip - flush with left border, full height, and matching left rounding */}
            <div
                className="absolute inset-y-0 left-0 w-[14px]"
                style={{
                    background: useGradient 
                        ? `linear-gradient(to bottom, ${finalColor1} 0%, ${finalColor1} 25%, ${finalColor2} 100%)`
                        : finalColor1,
                    borderTopLeftRadius: 16,
                    borderBottomLeftRadius: 16,
                }}
            />

            {/* Content */}
            <div className="flex-1 flex items-center h-full pl-3 pr-2 min-w-0 overflow-hidden">
                <div className="flex flex-col justify-center gap-0 min-w-0 flex-1">
                    {title && (
                        <div className="flex flex-col gap-0 min-w-0 mb-2">
                            <div className={`flex items-center gap-1 ${showInsights ? 'pr-8' : 'pr-2.5'}`}>
                                <span
                                    className="text-[12px] xl:text-[14px] 2xl:text-[16px] font-normal text-gray-900 truncate leading-tight overflow-hidden whitespace-nowrap text-ellipsis"
                                    style={{ fontFamily: "Roboto, sans-serif" }}
                                    title={title}
                                >
                                    {title}
                                </span>
                                {widget?.widgetInfo && (
                                    <Tooltip content={widget.widgetInfo} variant="widgetTitle">
                                        <span className="shrink-0">
                                            <InfoIcon />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                            {dateRangeLabel ? (
                                <div className="text-xs text-slate-500 leading-4 truncate pt-0.5">
                                    {dateRangeLabel}
                                </div>
                            ) : null}
                        </div>
                    )}
                    <div className="flex items-end gap-3 min-w-0">
                        {isNoDataError ? (
                            // No data state: show icon with round badge and message
                            <div className="flex items-center gap-2">
                                <div className="p-2">
                                    <NoDataIcon className="w-3 h-3 text-gray-600" />
                                </div>
                                <span className="text-gray-800 text-[16px] font-semibold leading-[22px]" style={{ fontFamily: "Roboto, sans-serif" }}>
                                    No Data Available
                                </span>
                            </div>
                        ) : (
                            // Regular value display
                            <div
                                className={`${error ? 'text-red-600 text-[12px] xl:text-[14px] 2xl:text-[16px] font-normal leading-tight' : 'text-gray-900 text-[16px] xl:text-[20px] 2xl:text-[24px] font-semibold leading-tight'} truncate`}
                                style={{ fontFamily: "Roboto, sans-serif" }}
                            >
                                {error || formatValue(currentValue)}
                            </div>
                        )}
                        {showCompare && (
                            <span
                                className={`flex shrink-0 items-center gap-1 text-[14px] font-normal leading-[22px] ${
                                    isImprovement ? "text-emerald-600" : "text-red-600"
                                }`}
                                style={{ fontFamily: "Roboto, sans-serif" }}
                                title={`vs ${formatCompareValue(compareValue)}`}
                            >
                                {isUp ? (
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 17l6-6 4 4 8-8"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M14 7h7v7"
                                        />
                                    </svg>
                                ) : (
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 7l6 6 4-4 8 8"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M14 17h7v-7"
                                        />
                                    </svg>
                                )}
                                <span className="truncate">vs {formatCompareValue(compareValue)}</span>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Icon on the right side */}
            {iconUrl ? (
                <div className="flex-shrink-0 w-8 xl:w-10 2xl:w-14 flex items-center justify-center mr-2">
                    <img src={iconUrl} alt="" className="h-6 w-6 xl:h-8 xl:w-8 2xl:h-12 2xl:w-12 object-contain" />
                </div>
            ) : (
                <div className="flex-shrink-0 w-2 mr-2" />
            )}

            {/* Actions: Insights button + 3-dot menu */}
            {widget && !hideActions && (
                <div className="absolute top-2 right-2 flex items-center">
                    {showInsights && (
                        <Tooltip className="inline-flex" content="Get Insights">
                            <button
                                onClick={insightsController.open}
                                className="p-1 rounded-full hover:bg-gray-100 hover:shadow-md transition-all duration-200 cursor-pointer"
                                style={{ color: '#1F8894' }}
                                aria-label="Get Insights"
                            >
                                <AIStarsIcon className="w-4 h-4" />
                            </button>
                        </Tooltip>
                    )}
                    <Popover.Root>
                        <Popover.Trigger asChild>
                            <button
                                className="p-1 rounded-full hover:bg-gray-100 transition-all duration-200 text-gray-500 hover:text-gray-700 cursor-pointer"
                                aria-label="Widget actions"
                            >
                                <ThreeDotsIcon className="w-4 h-4" />
                            </button>
                        </Popover.Trigger>
                        <Popover.Portal>
                            <Popover.Content
                                side="bottom"
                                align="end"
                                sideOffset={4}
                                className="z-50 bg-white shadow-lg rounded-md py-1 border border-gray-200 min-w-[140px]"
                                onOpenAutoFocus={(e) => e.preventDefault()}
                            >
                                {canEdit && (
                                    <button
                                        onClick={() => openEditDrawer(widget)}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-roboto"
                                    >
                                        <EditIcon className="w-4 h-4" />
                                        Edit
                                    </button>
                                )}
                                {canDuplicate && (
                                    <button
                                        onClick={() => openDuplicateDrawer(widget)}
                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-roboto"
                                    >
                                        <Copy className="w-4 h-4" color="#8FB4C2" />
                                        Duplicate
                                    </button>
                                )}
                                {canDelete && (
                                    <button
                                        onClick={handleDelete}
                                        disabled={isDeleting}
                                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 font-roboto ${isDeleting ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
                                    >
                                        {isDeleting ? (
                                            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                        ) : (
                                            <TrashIcon />
                                        )}
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                )}
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>
                </div>
            )}

            {widget && !hideActions && showInsights && (
                <WidgetInsights
                    widget={widget}
                    widgetData={data}
                    externalController={insightsController}
                    anchorRef={undefined}
                    hideTrigger={true}
                />
            )}
        </Card>
    );
}
